import { Controller } from "@hotwired/stimulus";
import { postMessageToPreviewIframes } from "../lib/preview_iframe_messaging";

export default class extends Controller {
  static values = { url: String, previewComposeUrl: String };
  static targets = [
    "subject",
    "message",
    "smsMessage",
    "cc",
    "bcc",
    "attachIcs",
    "spinner",
  ];

  connect() {
    this.timeout = null;
    this.inflight = 0;
    // Tracks whether the form has been edited since the preview iframe was
    // last refreshed; lets us skip a refresh on a clean Preview-tab click.
    this.dirty = false;
    this.previewHandler = async (e) => {
      if (!this.dirty) return;
      await this.flushPendingSave();
      postMessageToPreviewIframes({ type: "refresh-preview" });
      this.dirty = false;
    };
    this.element.addEventListener("composer-tabs:preview", this.previewHandler);

    this.submitHandler = () => {
      if (this.timeout) clearTimeout(this.timeout);
      // Cancel any in-flight autosave so it can't land after Continue submits.
      this.abortController?.abort();
    };
    document
      .getElementById("message-form")
      ?.addEventListener("submit", this.submitHandler);
  }

  disconnect() {
    if (this.timeout) clearTimeout(this.timeout);
    this.abortController?.abort();
    this.element.removeEventListener(
      "composer-tabs:preview",
      this.previewHandler,
    );
    document
      .getElementById("message-form")
      ?.removeEventListener("submit", this.submitHandler);
  }

  save() {
    this.dirty = true;
    if (this.timeout) clearTimeout(this.timeout);
    this.timeout = setTimeout(() => this.persist(), 500);
  }

  saveAndReload() {
    if (this.timeout) clearTimeout(this.timeout);
    this.persist().then(() => {
      postMessageToPreviewIframes({ type: "refresh-preview" });
    });
  }

  // Flush any pending or in-flight autosave. Used before refreshing the
  // preview so the latest keystrokes are on disk by the time the iframe
  // re-fetches its content.
  async flushPendingSave() {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
      await this.persist();
    } else if (this.currentPersist) {
      await this.currentPersist;
    }
  }

  persist() {
    if (!this.urlValue) return Promise.resolve();
    if (this.currentPersist) return this.currentPersist;
    this.currentPersist = this.#persist().finally(() => {
      this.currentPersist = null;
    });
    return this.currentPersist;
  }

  async #persist() {
    const body = this.collectFormBody();
    const token = document.querySelector('meta[name="csrf-token"]')?.content;

    // Cancel any earlier in-flight save so an older response can't land after
    // a newer one and clobber `urlValue` / form action.
    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;

    this.startSaving();
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, Accept: "application/json" },
        body,
        signal: controller.signal,
      });

      if (response.ok && this.isJsonResponse(response)) {
        const data = await response.json();
        this.adoptPersistedRecord(
          data,
          document.getElementById("message-form"),
        );
      }
    } catch (error) {
      if (error.name !== "AbortError") throw error;
    } finally {
      if (this.abortController === controller) this.abortController = null;
      this.finishSaving();
    }
  }

  // Renders the preview straight from current form state — never persists.
  // Works for both new (unsaved) drafts and existing ones, so the iframe
  // always reflects the user's latest edits, not the most recent autosave.
  async composePreview(frameId, channel, offset = 0) {
    if (!this.previewComposeUrlValue) return;
    const oldFrame = document.getElementById(frameId);
    if (!oldFrame) return;

    const body = this.collectFormBody();
    body.append("via", channel);
    body.append("offset", offset);
    const token = document.querySelector('meta[name="csrf-token"]')?.content;

    const response = await fetch(this.previewComposeUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": token, Accept: "text/html" },
      body,
    });
    if (!response.ok) return;

    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const newFrame = doc.getElementById(frameId);
    if (newFrame) oldFrame.replaceWith(newFrame);
    this.dirty = false;
  }

  paginatePreview(event) {
    event.preventDefault();
    const offset = parseInt(event.currentTarget.dataset.offset, 10) || 0;
    const channel = event.currentTarget.dataset.channel || "email";
    this.composePreview(`composer-preview-${channel}`, channel, offset);
  }

  collectFormBody() {
    const body = new FormData();
    if (this.hasSubjectTarget) body.append("subject", this.subjectTarget.value);
    if (this.hasMessageTarget) body.append("message", this.messageTarget.value);
    if (this.hasSmsMessageTarget)
      body.append("sms_message", this.smsMessageTarget.value);
    if (this.hasCcTarget) body.append("cc", this.ccTarget.value);
    if (this.hasBccTarget) body.append("bcc", this.bccTarget.value);
    if (this.hasAttachIcsTarget)
      body.append("attach_ics", this.attachIcsTarget.checked);

    const form = document.getElementById("message-form");
    const templateSelect = form?.querySelector(
      "[name='bulk_delivery[html_template_id]']",
    );
    if (templateSelect) body.append("html_template_id", templateSelect.value);
    return body;
  }

  isJsonResponse(response) {
    return (response.headers.get("Content-Type") || "").includes(
      "application/json",
    );
  }

  adoptPersistedRecord(data, form) {
    if (data.saveUrl) this.urlValue = data.saveUrl;
    if (form && data.formUrl) {
      form.setAttribute("action", data.formUrl);
      this.ensurePatchMethod(form);
    }
    if (data.editUrl) history.replaceState(null, "", data.editUrl);
    // Swap the preview turbo-frames from the frozen-attrs URL to the persisted-draft URL
    // so subsequent refresh-preview postMessages re-fetch fresh content from the DB.
    if (data.previewFrameUrls) {
      Object.entries(data.previewFrameUrls).forEach(([channel, url]) => {
        const frame = document.getElementById(`composer-preview-${channel}`);
        if (frame && frame.getAttribute("src") !== url)
          frame.setAttribute("src", url);
      });
    }
  }

  ensurePatchMethod(form) {
    let methodInput = form.querySelector("input[name='_method']");
    if (!methodInput) {
      methodInput = document.createElement("input");
      methodInput.type = "hidden";
      methodInput.name = "_method";
      form.insertBefore(methodInput, form.firstChild);
    }
    methodInput.value = "patch";
  }

  startSaving() {
    this.inflight++;
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove("hidden");
  }

  finishSaving() {
    this.inflight = Math.max(0, this.inflight - 1);
    if (this.inflight === 0 && this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden");
    }
  }
}
